import { History } from 'lucide-react';
import { Badge, Card, CardHeader, EmptyState } from '../../components/ui';
import { ruDate, ruDateTime } from '../../lib/format';
import type { AgentDataRevision } from './api';
import { Fact } from './parts';
import { BASIS_KIND_LABEL, LABEL, T, TITLE, say } from './texts';

/*
 * История разрешения на выборки (005, T139; FR-028: включение, выключение и изменение пишутся в журнал действий):
 * каждая запись (`AgentDataRevision`) — когда, автор, состояние, основание, клиенты (имена — с сервера), лимиты.
 * Сверху новые. Автора нет в базе (имя пустое) — строки автора нет. На компьютере сведения записи — в три колонки,
 * до 900 px — столбцом.
 */

function basisText(h: AgentDataRevision): string {
  if (!h.basis) return LABEL.noBasis;
  return say.basis(BASIS_KIND_LABEL[h.basis.kind], h.basis.ref, ruDate(h.basis.date));
}

export function HistoryView({ history, tz }: { history: readonly AgentDataRevision[]; tz: string }) {
  return (
    <Card>
      <CardHeader title={TITLE.history} description={T.historyIntro} />
      {history.length === 0 ? (
        <EmptyState icon={History} title={TITLE.historyEmpty} description={T.historyEmpty} />
      ) : (
        <ol aria-label={LABEL.changes} className="divide-y divide-line border-t border-line">
          {history.map((h) => (
            <li key={h.id} className="px-6 py-4 max-sm:px-4">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-[14px] font-semibold text-ink tabular-nums">
                  {ruDateTime(h.setAt, tz)}
                </span>
                {h.setBy.name && (
                  <span className="text-[13px] text-ink-2">
                    <span className="sr-only">{LABEL.author}: </span>
                    {h.setBy.name}
                  </span>
                )}
                <Badge tone={h.enabled ? 'ok' : 'neutral'} dot>
                  {h.enabled ? LABEL.stateOn : LABEL.stateOff}
                </Badge>
              </div>
              <dl className="mt-2 grid grid-cols-3 gap-x-6 gap-y-2 max-tablet:grid-cols-1">
                <Fact label={LABEL.basisShort}>{basisText(h)}</Fact>
                <Fact label={LABEL.clientsShort}>
                  {h.clients.length === 0 ? LABEL.noClients : h.clients.map((c) => c.name).join(', ')}
                </Fact>
                <Fact label={LABEL.limitsShort}>
                  <span className="tabular-nums">{say.limits(h.limits.perResponse, h.limits.perDay)}</span>
                </Fact>
              </dl>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
